import React, { useState } from "react";
import { NavLink, Routes, Route } from "react-router-dom";
import Villas from "./Villas.js";
import Guests from "./Guests.js";
import Orders from "./Orders.js";
import Menus from "./Menus.js";
import Accounting from "./Accounting.js";
import OrderAudit from "./OrderAudit.js";
import { adminLogin, getAdminRole, getAdminToken, setAdminToken } from "./api";
import "./ManagementStyles.css";

function ManagementPage() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/orders/api';
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getAdminToken()));
    const [adminRole, setAdminRole] = useState(getAdminRole());

    const canAccessBasicManagement = adminRole === "admin_basic" || adminRole === "admin_super";
    const canAccessSuperManagement = adminRole === "admin_super";

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setLoginError("");
        try {
            const payload = await adminLogin(baseUrl, username, password);
            setAdminRole(payload.role || "");
            setIsAuthenticated(true);
        } catch (error) {
            setLoginError(error.message || "Login failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        setAdminToken("");
        setAdminRole("");
        setIsAuthenticated(false);
    };

    if (!isAuthenticated) {
        return (
            <main className="management-login-shell">
                <h2>Admin Login</h2>
                <form onSubmit={handleLogin} style={{ display: "grid", gap: "12px" }}>
                    <input
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Signing in..." : "Sign in"}
                    </button>
                    {loginError ? <p style={{ color: "red" }}>{loginError}</p> : null}
                </form>
            </main>
        );
    }

    return (
        <div className="management-shell">
            <nav className="management-sidebar">
                <div className="management-sidebar-title">Management</div>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Role: {adminRole || "unknown"}</span>
                </div>

                <div className="management-sidebar-links">
                    {canAccessSuperManagement && (
                        <NavLink
                            to="/management/villas"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Villa Management
                        </NavLink>
                    )}

                    {canAccessBasicManagement && (
                        <NavLink
                            to="/management/guests"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Guest Management
                        </NavLink>
                    )}

                    {canAccessBasicManagement && (
                        <NavLink
                            to="/management/orders"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Order Management
                        </NavLink>
                    )}

                    {canAccessSuperManagement && (
                        <NavLink
                            to="/management/menus"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Menu Management
                        </NavLink>
                    )}

                    {canAccessSuperManagement && (
                        <NavLink
                            to="/management/accounting"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Accounting
                        </NavLink>
                    )}

                    {canAccessSuperManagement && (
                        <NavLink
                            to="/management/order-audit"
                            className={({ isActive }) => `management-sidebar-link${isActive ? " active" : ""}`}
                        >
                            Order Audit
                        </NavLink>
                    )}
                </div>

                <button onClick={handleLogout} className="management-sidebar-logout">
                    Logout
                </button>
            </nav>

            <main className="management-main">
                <Routes>
                    <Route path="/villas" element={canAccessSuperManagement ? <Villas/> : <p>Access denied.</p>} />
                    <Route path="/guests" element={canAccessBasicManagement ? <Guests/> : <p>Access denied.</p>} />
                    <Route path="/menus" element={canAccessSuperManagement ? <Menus/> : <p>Access denied.</p>} />
                    <Route path="/orders" element={canAccessBasicManagement ? <Orders/> : <p>Access denied.</p>} />
                    <Route path="/accounting" element={canAccessSuperManagement ? <Accounting/> : <p>Access denied.</p>} />
                    <Route path="/order-audit" element={canAccessSuperManagement ? <OrderAudit/> : <p>Access denied.</p>} />
                    <Route
                        path="*"
                        element={
                            canAccessBasicManagement
                                ? <p>Please select a management section from the navigation.</p>
                                : <p>Access denied.</p>
                        }
                    />
                </Routes>
            </main>
        </div>
    );
}

export default ManagementPage;
